import type { GridColumnVisibilityModel } from '@mui/x-data-grid-premium';
import type { TrafficSource } from '../services/traffic-sources.service';

// Lead fields carried on the incoming postback URL (celatrack insurance postbacks).
export const POSTBACK_LEAD_FIELDS = [
  'age', 'country', 'homeOwnership', 'insured', 'currentInsuranceCompany', 'multiDriver', 'multiVehicle',
] as const;
export type PostbackLeadField = (typeof POSTBACK_LEAD_FIELDS)[number];

const TEMPLATE_RE = /^\{\{.*\}\}$/; // unreplaced macro like {{currentInsuranceCompany}}

/**
 * Parse the lead attributes out of an incoming postback URL's query string.
 * Client-only / calculated — nothing is stored for these. Unreplaced macros and
 * missing params are returned as '' so columns render blank.
 */
export function parsePostbackLead(url?: string | null): Record<PostbackLeadField, string> {
  const out = Object.fromEntries(POSTBACK_LEAD_FIELDS.map(f => [f, ''])) as Record<PostbackLeadField, string>;
  if (!url) return out;
  const qIndex = url.indexOf('?');
  if (qIndex === -1) return out;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(url.slice(qIndex + 1));
  } catch {
    return out;
  }
  for (const field of POSTBACK_LEAD_FIELDS) {
    const raw = params.get(field);
    if (raw == null) continue;
    const val = raw.trim();
    if (!val || TEMPLATE_RE.test(val)) continue;
    out[field] = val;
  }
  return out;
}

/**
 * Map v1..v10 → the real column name configured on a traffic source's custom
 * params (e.g. v2 → "Campaign ID"). Returns {} when no source / no config.
 */
export function trafficSourceVarNames(ts?: TrafficSource | null): Record<string, string> {
  const map: Record<string, string> = {};
  const custom = ts?.config?.params?.custom;
  if (!Array.isArray(custom)) return map;
  for (const c of custom) {
    if (c?.index && c?.name) map[`v${c.index}`] = c.name;
  }
  return map;
}

/**
 * The fields a visibility model switches off. Passing this to a save handler is what
 * makes the header's "Hide column" item work — without one it silently does nothing.
 */
export function hiddenFrom(model: GridColumnVisibilityModel): string[] {
  return Object.entries(model).filter(([, shown]) => !shown).map(([field]) => field);
}
